/**
 * SuiviTravauxTile — Batch #14.213
 *
 * Vue admin complète des work orders pour un master :
 *   - Liste filtrable par statut
 *   - Création nouveau travail (CRUD complet)
 *   - Édition / suppression / changement statut
 *   - Ajout / suppression factures (texte uniquement)
 *   - Upload / suppression photos (GridFS)
 *   - Export rapport (passe par /admin/work-orders/reports/pdf)
 *
 * L'admin agit toujours dans le contexte du master sélectionné.
 */
import { ToneSection as Section } from "@/components/shared/ToneSection";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { ClipboardList, Plus, Trash2, Save, Receipt, Camera, Loader2, Wrench, ArrowLeft, FileText, RefreshCw } from "lucide-react";
import { API, authH } from "@/api";

const STATUS_OPTIONS = [
  { v: "open", label: "🟦 Ouvert", color: "blue" },
  { v: "scheduled", label: "📅 Planifié", color: "violet" },
  { v: "in_progress", label: "🔧 En cours", color: "amber" },
  { v: "done", label: "✅ Terminé", color: "emerald" },
  { v: "cancelled", label: "❌ Annulé", color: "slate" },
];

const PRIORITY_OPTIONS = [
  { v: "low", label: "Basse" },
  { v: "normal", label: "Normale" },
  { v: "high", label: "Haute" },
  { v: "urgent", label: "Urgent" },
];

const CATEGORY_OPTIONS = [
  { v: "plomberie", label: "Plomberie" },
  { v: "electricite", label: "Électricité" },
  { v: "structure", label: "Structure" },
  { v: "revetement", label: "Revêtement" },
  { v: "exterieur", label: "Extérieur" },
  { v: "chauffage", label: "Chauffage" },
  { v: "general", label: "Général" },
];

const statusBadge = (s) => {
  const opt = STATUS_OPTIONS.find((x) => x.v === s) || STATUS_OPTIONS[0];
  const colorMap = {
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${colorMap[opt.color]}`}>
      {opt.label}
    </span>
  );
};

export default function SuiviTravauxTile({ masterId }) {
  const [view, setView] = useState("list"); // list | edit | new
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedWo, setSelectedWo] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ master_id: masterId });
      if (filterStatus) params.set("status", filterStatus);
      const r = await axios.get(`${API}/admin/work-orders?${params.toString()}`, { headers: authH() });
      setWorkOrders(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [masterId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    setExportBusy(true);
    try {
      const r = await axios.get(
        `${API}/admin/work-orders/reports/pdf?master_id=${masterId}`,
        { headers: authH(), responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `suivi-travaux-${masterId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport PDF téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur export PDF");
    } finally {
      setExportBusy(false);
    }
  };

  if (view === "new" || view === "edit") {
    return (
      <WorkOrderEditor
        masterId={masterId}
        workOrder={selectedWo}
        onDone={() => { setView("list"); setSelectedWo(null); load(); }}
        onCancel={() => { setView("list"); setSelectedWo(null); }}
      />
    );
  }

  return (
    <div className="bg-white border-2 border-amber-200 rounded-2xl shadow-sm overflow-hidden" data-testid="suivi-travaux-tile">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-amber-50 to-amber-100 border-b border-amber-200 flex items-center gap-3">
        <Wrench className="w-6 h-6 text-amber-700" />
        <div className="flex-1">
          <h2 className="text-base font-bold text-slate-900">Suivi des travaux</h2>
          <p className="text-xs text-slate-600 mt-0.5">
            Entrepreneur · Soumissions · Factures · Photos — pour ce dossier client
          </p>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg hover:bg-white/60 transition"
          aria-label="Actualiser"
          data-testid="suivi-travaux-refresh"
        >
          <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? "animate-spin" : ""}`} />
        </button>
        <button
          onClick={exportPdf}
          disabled={exportBusy}
          className="text-xs px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1 font-semibold disabled:opacity-50"
          data-testid="suivi-travaux-export-pdf"
        >
          {exportBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
          Rapport PDF
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Filtres + nouveau */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setFilterStatus("")}
              className={`text-[11px] px-2.5 py-1 rounded border ${
                filterStatus === "" ? "bg-amber-600 text-white border-amber-700" : "bg-white text-slate-600 border-slate-200"
              }`}
              data-testid="suivi-travaux-filter-all"
            >
              Tous ({workOrders.length})
            </button>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.v}
                type="button"
                onClick={() => setFilterStatus(s.v)}
                className={`text-[11px] px-2.5 py-1 rounded border ${
                  filterStatus === s.v ? "bg-amber-600 text-white border-amber-700" : "bg-white text-slate-600 border-slate-200"
                }`}
                data-testid={`suivi-travaux-filter-${s.v}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { setSelectedWo(null); setView("new"); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 active:scale-95 transition"
            data-testid="suivi-travaux-new-btn"
          >
            <Plus className="w-4 h-4" /> Nouveau travail
          </button>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
        ) : workOrders.length === 0 ? (
          <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
            <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-500">Aucun travail enregistré pour ce dossier</div>
            <div className="text-[11px] text-slate-400 mt-1">Cliquez sur « Nouveau travail » pour commencer</div>
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {workOrders.map((wo) => (
              <button
                key={wo.id}
                type="button"
                onClick={() => { setSelectedWo(wo); setView("edit"); }}
                className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 hover:border-amber-300 hover:shadow-sm transition"
                data-testid={`suivi-travaux-item-${wo.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-800 truncate">{wo.title || "(sans titre)"}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-2">
                      {statusBadge(wo.status)}
                      {wo.priority === "urgent" && <span className="text-rose-600 font-bold">🚨 URGENT</span>}
                      {wo.contractor_name && <span>· {wo.contractor_name}</span>}
                      {wo.invoices?.length > 0 && (
                        <span className="text-emerald-700 font-semibold">
                          · {wo.invoices.length} fact. · {Number(wo.invoices_total || 0).toFixed(2)} $
                        </span>
                      )}
                    </div>
                    {wo.description && <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">{wo.description}</p>}
                  </div>
                  <div className="text-[10px] text-slate-400 shrink-0">
                    {wo.created_at && new Date(wo.created_at).toLocaleDateString("fr-CA")}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Editor (create + edit) — Admin
// ════════════════════════════════════════════════════════════════════════════
function WorkOrderEditor({ masterId, workOrder, onDone, onCancel }) {
  const isEdit = !!workOrder;
  const [form, setForm] = useState({
    title: workOrder?.title || "",
    description: workOrder?.description || "",
    category: workOrder?.category || "general",
    priority: workOrder?.priority || "normal",
    status: workOrder?.status || "open",
    contractor_name: workOrder?.contractor_name || "",
    contractor_phone: workOrder?.contractor_phone || "",
    contractor_email: workOrder?.contractor_email || "",
    quote_amount: workOrder?.quote_amount ?? "",
    quote_received_at: workOrder?.quote_received_at || "",
    quote_accepted: !!workOrder?.quote_accepted,
    started_at: workOrder?.started_at || "",
    completed_at: workOrder?.completed_at || "",
    estimated_duration_days: workOrder?.estimated_duration_days ?? "",
    actual_duration_days: workOrder?.actual_duration_days ?? "",
    damage_description: workOrder?.damage_description || "",
    cause: workOrder?.cause || "",
    impact: workOrder?.impact || "",
    insurance_claim_number: workOrder?.insurance_claim_number || "",
    notes: workOrder?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [woId, setWoId] = useState(workOrder?.id || null);
  const [invoices, setInvoices] = useState(workOrder?.invoices || []);
  const [photos, setPhotos] = useState(workOrder?.photos || []);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invForm, setInvForm] = useState({ supplier: "", invoice_number: "", amount: "", description: "" });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quote_amount: form.quote_amount === "" ? null : Number(form.quote_amount),
        estimated_duration_days: form.estimated_duration_days === "" ? null : Number(form.estimated_duration_days),
        actual_duration_days: form.actual_duration_days === "" ? null : Number(form.actual_duration_days),
      };
      let r;
      if (isEdit || woId) {
        r = await axios.put(`${API}/admin/work-orders/${woId || workOrder.id}`, payload, { headers: authH() });
      } else {
        // Admin create — passe master_id en query
        r = await axios.post(`${API}/admin/work-orders?master_id=${masterId}`, payload, { headers: authH() });
        setWoId(r.data.id);
      }
      toast.success(isEdit ? "Travail mis à jour" : "Travail créé");
      setInvoices(r.data.invoices || invoices);
      setPhotos(r.data.photos || photos);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!woId && !workOrder?.id) return onCancel();
    if (!window.confirm("Supprimer ce travail définitivement ?")) return;
    try {
      await axios.delete(`${API}/admin/work-orders/${woId || workOrder.id}`, { headers: authH() });
      toast.success("Travail supprimé");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const addInvoice = async () => {
    if (!invForm.supplier.trim() || invForm.amount === "") {
      toast.error("Fournisseur et montant requis");
      return;
    }
    if (!woId && !workOrder?.id) {
      await save();
      if (!woId && !workOrder?.id) return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/work-orders/${woId || workOrder.id}/invoices`,
        { ...invForm, amount: Number(invForm.amount) },
        { headers: authH() },
      );
      setInvoices((prev) => [...prev, r.data]);
      setInvForm({ supplier: "", invoice_number: "", amount: "", description: "" });
      setShowInvoiceForm(false);
      toast.success("Facture ajoutée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const removeInvoice = async (invId) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      await axios.delete(`${API}/admin/work-orders/${woId || workOrder.id}/invoices/${invId}`, { headers: authH() });
      setInvoices((prev) => prev.filter((i) => i.id !== invId));
      toast.success("Facture supprimée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const uploadPhoto = async (file, phase = "other") => {
    if (!woId && !workOrder?.id) {
      toast.error("Enregistrez d'abord le travail");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(
        `${API}/admin/work-orders/${woId || workOrder.id}/photos?phase=${phase}`,
        fd,
        { headers: { ...authH(), "Content-Type": "multipart/form-data" } },
      );
      setPhotos((prev) => [...prev, r.data]);
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload échoué");
    }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await axios.delete(`${API}/admin/work-orders/${woId || workOrder.id}/photos/${photoId}`, { headers: authH() });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success("Photo supprimée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const invoicesTotal = invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  return (
    <div className="bg-white border-2 border-amber-200 rounded-2xl shadow-sm overflow-hidden" data-testid="suivi-travaux-editor">
      <div className="px-5 py-4 bg-gradient-to-r from-amber-50 to-amber-100 border-b border-amber-200 flex items-center gap-3">
        <Wrench className="w-6 h-6 text-amber-700" />
        <div className="flex-1">
          <h2 className="text-base font-bold text-slate-900">
            {isEdit ? `Modifier — ${workOrder?.title || ""}` : "Nouveau travail"}
          </h2>
          <p className="text-xs text-slate-600 mt-0.5">Tous les champs sont sauvegardés ensemble</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/60"
          data-testid="suivi-travaux-editor-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à la liste
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* BLOC 1 : Identification */}
        <Section title="📋 Identification" tone="amber">
          <FieldLabel label="Titre *">
            <input type="text" value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="Ex: Réparation chauffe-eau Apt 1A" className="cc-input" maxLength={200} data-testid="suivi-travaux-title" />
          </FieldLabel>
          <FieldLabel label="Description">
            <textarea rows={2} value={form.description} onChange={(e) => upd("description", e.target.value)} className="cc-input" maxLength={2000} data-testid="suivi-travaux-description" />
          </FieldLabel>
          <div className="grid grid-cols-3 gap-2">
            <FieldLabel label="Catégorie">
              <select value={form.category} onChange={(e) => upd("category", e.target.value)} className="cc-input" data-testid="suivi-travaux-category">
                {CATEGORY_OPTIONS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Priorité">
              <select value={form.priority} onChange={(e) => upd("priority", e.target.value)} className="cc-input" data-testid="suivi-travaux-priority">
                {PRIORITY_OPTIONS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Statut">
              <select value={form.status} onChange={(e) => upd("status", e.target.value)} className="cc-input" data-testid="suivi-travaux-status">
                {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </FieldLabel>
          </div>
        </Section>

        {/* BLOC 2 : Entrepreneur */}
        <Section title="👷 Entrepreneur" tone="blue">
          <FieldLabel label="Nom de l'entrepreneur">
            <input type="text" value={form.contractor_name} onChange={(e) => upd("contractor_name", e.target.value)} className="cc-input" maxLength={200} data-testid="suivi-travaux-contractor-name" />
          </FieldLabel>
          <div className="grid grid-cols-2 gap-2">
            <FieldLabel label="Téléphone">
              <input type="tel" value={form.contractor_phone} onChange={(e) => upd("contractor_phone", e.target.value)} className="cc-input" maxLength={30} />
            </FieldLabel>
            <FieldLabel label="Courriel">
              <input type="email" value={form.contractor_email} onChange={(e) => upd("contractor_email", e.target.value)} className="cc-input" maxLength={120} />
            </FieldLabel>
          </div>
        </Section>

        {/* BLOC 3 : Soumission */}
        <Section title="💰 Soumission / Devis" tone="emerald">
          <div className="grid grid-cols-2 gap-2">
            <FieldLabel label="Montant ($)">
              <input type="number" step="0.01" min="0" value={form.quote_amount} onChange={(e) => upd("quote_amount", e.target.value)} className="cc-input font-mono" />
            </FieldLabel>
            <FieldLabel label="Reçue le">
              <input type="date" value={form.quote_received_at?.slice(0, 10) || ""} onChange={(e) => upd("quote_received_at", e.target.value)} className="cc-input" />
            </FieldLabel>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={form.quote_accepted} onChange={(e) => upd("quote_accepted", e.target.checked)} />
            Soumission acceptée
          </label>
        </Section>

        {/* BLOC 4 : Période */}
        <Section title="📅 Période" tone="violet">
          <div className="grid grid-cols-2 gap-2">
            <FieldLabel label="Date début">
              <input type="date" value={form.started_at?.slice(0, 10) || ""} onChange={(e) => upd("started_at", e.target.value)} className="cc-input" />
            </FieldLabel>
            <FieldLabel label="Date fin">
              <input type="date" value={form.completed_at?.slice(0, 10) || ""} onChange={(e) => upd("completed_at", e.target.value)} className="cc-input" />
            </FieldLabel>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldLabel label="Durée estimée (jours)">
              <input type="number" min="0" value={form.estimated_duration_days} onChange={(e) => upd("estimated_duration_days", e.target.value)} className="cc-input font-mono" />
            </FieldLabel>
            <FieldLabel label="Durée réelle (jours)">
              <input type="number" min="0" value={form.actual_duration_days} onChange={(e) => upd("actual_duration_days", e.target.value)} className="cc-input font-mono" />
            </FieldLabel>
          </div>
        </Section>

        {/* BLOC 5 : Dégâts */}
        <Section title="⚠️ Dégâts / Contexte" tone="rose">
          <FieldLabel label="Description des dégâts">
            <textarea rows={2} value={form.damage_description} onChange={(e) => upd("damage_description", e.target.value)} className="cc-input" maxLength={2000} />
          </FieldLabel>
          <FieldLabel label="Cause">
            <input type="text" value={form.cause} onChange={(e) => upd("cause", e.target.value)} className="cc-input" maxLength={500} />
          </FieldLabel>
          <FieldLabel label="Impact">
            <textarea rows={2} value={form.impact} onChange={(e) => upd("impact", e.target.value)} className="cc-input" maxLength={2000} />
          </FieldLabel>
          <FieldLabel label="N° réclamation assurance">
            <input type="text" value={form.insurance_claim_number} onChange={(e) => upd("insurance_claim_number", e.target.value)} className="cc-input" maxLength={80} />
          </FieldLabel>
        </Section>

        {/* BLOC 6 : Factures */}
        <Section title={`🧾 Factures (${invoices.length}) · Total ${invoicesTotal.toFixed(2)} $`} tone="cyan">
          {invoices.length > 0 && (
            <div className="space-y-1">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-start gap-2 bg-cyan-50 border border-cyan-200 rounded p-2" data-testid={`suivi-travaux-invoice-${inv.id}`}>
                  <Receipt className="w-3.5 h-3.5 text-cyan-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800">
                      {inv.supplier} · <span className="text-emerald-700">{Number(inv.amount).toFixed(2)} $</span>
                      {inv.invoice_number && <span className="text-slate-500 ml-1 font-mono">#{inv.invoice_number}</span>}
                    </div>
                    {inv.description && <div className="text-[10px] text-slate-600">{inv.description}</div>}
                  </div>
                  <button type="button" onClick={() => removeInvoice(inv.id)} className="text-rose-500 hover:text-rose-700" data-testid={`suivi-travaux-invoice-remove-${inv.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {showInvoiceForm ? (
            <div className="bg-white border border-cyan-300 rounded-lg p-2 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Fournisseur *" value={invForm.supplier} onChange={(e) => setInvForm({...invForm, supplier: e.target.value})} className="cc-input text-xs" maxLength={200} data-testid="suivi-travaux-inv-supplier" />
                <input placeholder="N° facture" value={invForm.invoice_number} onChange={(e) => setInvForm({...invForm, invoice_number: e.target.value})} className="cc-input text-xs" maxLength={80} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" step="0.01" placeholder="Montant *" value={invForm.amount} onChange={(e) => setInvForm({...invForm, amount: e.target.value})} className="cc-input text-xs font-mono" data-testid="suivi-travaux-inv-amount" />
                <input placeholder="Description" value={invForm.description} onChange={(e) => setInvForm({...invForm, description: e.target.value})} className="cc-input text-xs" maxLength={500} />
              </div>
              <div className="flex gap-1 justify-end">
                <button type="button" onClick={() => setShowInvoiceForm(false)} className="text-xs px-2 py-1 rounded border">Annuler</button>
                <button type="button" onClick={addInvoice} className="text-xs px-2.5 py-1 rounded bg-cyan-600 text-white font-semibold" data-testid="suivi-travaux-inv-save">Ajouter</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setShowInvoiceForm(true)} className="text-xs text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1" data-testid="suivi-travaux-inv-add-btn">
              <Plus className="w-3.5 h-3.5" /> Ajouter une facture
            </button>
          )}
        </Section>

        {/* BLOC 7 : Photos */}
        <Section title={`📷 Photos (${photos.length})`} tone="indigo">
          {photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative aspect-square bg-slate-100 rounded overflow-hidden group" data-testid={`suivi-travaux-photo-${p.id}`}>
                  <img
                    src={`${API}/work-orders/photos/${p.gridfs_id}`}
                    alt={p.filename}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <div className="absolute top-1 left-1 text-[8px] bg-black/60 text-white px-1.5 py-0.5 rounded uppercase">{p.phase}</div>
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-600/90 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                    data-testid={`suivi-travaux-photo-remove-${p.id}`}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {(woId || workOrder?.id) ? (
            <label className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 cursor-pointer" data-testid="suivi-travaux-photo-upload-label">
              <Camera className="w-3.5 h-3.5" /> Ajouter une photo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0], "other")}
                data-testid="suivi-travaux-photo-input"
              />
            </label>
          ) : (
            <div className="text-[11px] text-slate-400 italic">Enregistrez d&apos;abord pour téléverser des photos</div>
          )}
        </Section>

        {/* BLOC 8 : Notes */}
        <Section title="📝 Notes générales" tone="slate">
          <textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" maxLength={3000} data-testid="suivi-travaux-notes" />
        </Section>

        {/* FOOTER ACTIONS */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
          <div className="flex gap-1">
            {isEdit && (
              <button type="button" onClick={onDelete} className="text-xs px-3 py-1.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1" data-testid="suivi-travaux-delete">
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            )}
          </div>
          <div className="flex gap-1">
            <button type="button" onClick={onCancel} className="text-xs px-3 py-1.5 rounded border">Annuler</button>
            <button type="button" onClick={save} disabled={busy} className="text-xs px-4 py-1.5 rounded bg-amber-600 text-white font-bold hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="suivi-travaux-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {isEdit || woId ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

